import React, { useCallback, useEffect, useState } from 'react';
import BackBtn from '../../Button/BackBtn';
import Spacing from '../../Spacing';
import { Search, TestTubeDiagonal } from 'lucide-react';
import DataTablePagination from '../../DataTablePagination';
import { can_visit, debounce, generatePatientTableData, getDateString, getPractice, getSpeciality, notify, numberWithCommas, userMessageTemplates } from '../../../helpers';
import { useDocumentTitle } from '../../../hooks/customDocumentTitleHook';
import LabFollowUpDataTable from '../../DataTable/LabFollowUpDataTable';
import { fetchFUPatients } from '../../../services/labFollowUp';
import Filter from '../../Filter';
import FilterSummary from '../../UI/FilterSummary';
import { fetchFilterData } from '../../../services/saveFilter';
import SaveFilter from '../../Filter/SaveFilter';
import FilterSaveConfirmationModal from '../../Modal/FilterSaveConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { addToWaitingRoom } from '../../../services/patients';

export default function LabReportMissed({selectedPracticeChange, fetchChangePracticeData, smsLimit, setSmsLimit, setLetterLimit, changePracticeSpeciality, waitingRoomProviders}) {

  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");
  let specilityId = getSpeciality()?.id || 32;
  if(selectedPracticeChange != null && fetchChangePracticeData){
    location_id = selectedPracticeChange?.location_id;
    account_id = selectedPracticeChange?.account_id;
    specilityId = selectedPracticeChange?.specialities[0]?.id || 32;
  }
  if (changePracticeSpeciality != null) {
    specilityId = changePracticeSpeciality?.id || 32;
  }
  let perPage = 10;
  if(can_visit('ehr_veradigm')) { specilityId = 32; perPage = 20; }
  let dateStr = getDateString();

  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(true);
  const [messageTemplate] = useState(userMessageTemplates());
  const [data, setData] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    speciality_id: specilityId,
    page: 1,
    per_page: perPage || 10,
    search_term: "",
    first_name: "",
    last_name: "",
    dob: "",
    mobile_phone: "",
    city: "",
    state: "",
    zip: "",
    insurance_ids: [],
    start_age: "",
    end_age: "",
    last_visit: "",
    sex: "",
    visit_day_query_type: "",
    visited_last_days: "",
    not_visited_last_days: "",
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);

  const [selectedInsuranceOptions, setSelectedInsuranceOptions] = useState([]);
  const [patientFormData, setPatientFormData] = useState(filter);
  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });

  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [selectedPatientData, setSelectedPatientData] = useState([]);
  const [showSave, setShowSave] = useState(true);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const loadData = async (getFilter) => {
    setFilter(getFilter);
    let isCanceled = false;

    try {
      const response = await fetchFUPatients(getFilter);
      if (response) {
        const generateData = generatePatientTableData(response?.data?.results);
        setData(generateData);
        setDataCount({
          totalCount: response?.data?.count,
          totalPages: response?.data?.total_pages,
        });
      }
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") {
        isCanceled = true;
      } else {
        // console.log("🚀 ~ loadLabReportPatientData ~ error:", error);
      }
    } finally {
      if (!isCanceled) {
        setDataLoading(false);
      }
    }
  };

  useEffect(() => {
    loadData(filter);
  }, [filter]);

  useEffect(() => {
    const sameLocation = filter.location_id === location_id;
    const sameAccount = filter.account_id === account_id;
    const sameSpec = filter.speciality_id === specilityId;

    if (sameLocation && sameAccount && sameSpec) return;

    setFilter({
      ...filter,
      location_id: location_id,
      account_id: account_id,
      speciality_id: specilityId,
    });
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
    setDataLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location_id, account_id, specilityId]);

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPatientData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPatientData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const [isFilterCleared, setIsFilterCleared] = useState(false);

  const handleClearFilters = () => {
    setIsFilterCleared(true);
  };

  const handleResetFilterStatus = () => {
    setIsFilterCleared(false);
  };

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setDataLoading(true);
    debouncedSetFilter(newFilter);
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPatientData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setDataLoading(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setDataLoading(true);
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const clearFilterField = (field) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: "",  // Clear the specified field in filter state
    }));
    setPatientFormData((prevData) => ({
      ...prevData,
      [field]: "",  // Clear the specified field in patientFormData
    }));
  };
  
  const loadFilters = async () => {
    const filters = {
      account_id: account_id,
      location_id: location_id,
      user_id: user_id,
      filter_type: "lab-report-missed",
    };

    try {
      const response = await fetchFilterData(filters);
      if (response) {
        if (response?.detail) {
          notify("error", response?.detail);
        } else {
          setFilterData(response?.data);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ loadFilters ~ error:", error);
    }
  };

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id]);

  const handleAddToWaitingRoom = async (data, id) => {
    const postDataObject = {
      account_id: account_id,
      location_id: location_id,
      speciality_id: specilityId,
      patient_id: data.id,
      dos: dateStr,
      care_gap: [],
      review_sent: false,
      provider_id: id
    };

    try {
      await addToWaitingRoom(postDataObject);
      notify("success", "Patient added to waiting room successfully");
    } catch (error) {
      notify("error", error?.response?.data?.detail);
    }
  };

  const handleReferralNavigation = (data) => {
    navigate(`/new-referral`, { state: { data } });
  };

  useDocumentTitle("Lab report follow up missed");

  return (
    <>
      <BackBtn href="/" btnText="Back to home" />
      <Spacing lg="0" sm="24" />
      <div className="docpro_page_heading_wrap">
        <h2 className="docpro_page_heading docpro_fs_24">
          <i
            className="docpro_white_color"
            style={{ backgroundColor: '#e93d82' }}
          >
            <TestTubeDiagonal width={18} height={18} />
          </i>
          Lab Report f/u Missed
          <span>{numberWithCommas(dataCount.totalCount)}</span>
        </h2>
      </div>
      <Spacing lg="15" sm="15" />
      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings">
          <form className="docpro_search_form width_100">
            <input
              type="search"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>

          <div className="docpro_data_table_btns small_filter_btns3 p-0">
            {filterData?.length > 0 && (
              <SaveFilter
                account_id={account_id}
                location_id={location_id}
                user_id={user_id}
                filterData={filterData}
                setDataLoading={setDataLoading}
                setPatientFormData={setPatientFormData}
                setSelectedInsuranceOptions={setSelectedInsuranceOptions}
                setFilter={setFilter}
                setShowSave={setShowSave}
                loadFilters={loadFilters}
                reloadData={loadData}
                reloadDataFilter={defaultFilter}
                previousScrollPosition={previousScrollPosition}
                setPreviousScrollPosition={setPreviousScrollPosition}
              />
            )}
            <Filter
              filterType="patient"
              reloadData={loadData}
              reloadDataFilter={filter}
              setDataLoading={setDataLoading}
              isFilterCleared={isFilterCleared}
              onResetFilterStatus={handleResetFilterStatus}
              selectedInsuranceOptions={selectedInsuranceOptions}
              setSelectedInsuranceOptions={setSelectedInsuranceOptions}
              patientFormData={patientFormData}
              setPatientFormData={setPatientFormData}
              setShowSave={setShowSave}
            />
          </div>
        </div>

        <div className="docpro_filter_summary">
          <FilterSummary 
            filter={filter}
            setFilter={setFilter}
            loadData={loadData}
            onClearFilters={handleClearFilters}
            setSelectedInsuranceOptions={setSelectedInsuranceOptions}
            clearFilterField={clearFilterField}
            patientFormData={patientFormData}
            setPatientFormData={setPatientFormData}
            setSaveConfirmModal={setSaveConfirmModal}
            showSave={showSave}
            setShowSave={setShowSave}
            setPreviousScrollPosition={setPreviousScrollPosition}
            setDataLoading={setDataLoading}
          /> 
        </div>
        <LabFollowUpDataTable 
          tableData={data}
          handleSelectAll={() => handleSelectAll(data)}
          handleCheckboxChange={handleCheckboxChange}
          selectAll={selectAll}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={loadData}
          reloadDataFilter={filter}
          messageTemplate={messageTemplate}
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          dataLoading={dataLoading}
          setDataLoading={setDataLoading}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatientData}
          setSelectedPatientData={setSelectedPatientData}
          setLetterLimit={setLetterLimit}
          handleAddToWaitingRoom={handleAddToWaitingRoom}
          handleReferralNavigation={handleReferralNavigation}
          user_id={user_id}
          waitingRoomProviders={waitingRoomProviders}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(data.length > 0 && !dataLoading) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>

      {saveConfirmModal && (
        <FilterSaveConfirmationModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          filter_type="lab-report-missed"
          filter={filter}
          openModal={saveConfirmModal}
          setOpenModal={setSaveConfirmModal}
          setShowSave={setShowSave}
          loadFilters={loadFilters}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
